import { useCallback, useEffect, useRef } from "react";
import clsx from "clsx";
import type { ResolvedTravelSegment } from "../../hooks/useResolvedTravel";
import { useTripStore } from "../../store/tripStore";
import { useUIStore } from "../../store/uiStore";
import { TRAVEL_MODE_ICONS, TRAVEL_MODE_LABELS } from "../../utils/geo";
import { TRAVEL_RAIL_X_PX } from "../../utils/calendarLayout";
import { directionsMapsUrl } from "../../utils/poiMaps";
import { Button } from "../Button";
import { CalendarAnchoredPopover } from "./CalendarActionToolbar";
import { TravelModePicker, type TravelModeChoice } from "./TravelModePicker";

const OPEN_DELAY_MS = 150;
const CLOSE_DELAY_MS = 200;
/** Gap height (px) needed before the duration label is drawn beside the pin. */
const LABEL_MIN_GAP_PX = 18;
const KM_TO_MILES = 0.621371;
/** Space between pin and card, bridged by the card's callout arrow. */
const CARD_OFFSET_PX = 8;

interface TravelConnectorProps {
  segment: ResolvedTravelSegment;
  /** Y of the previous block's end and the next block's start, in px */
  gapTop: number;
  gapBottom: number;
  isReadOnly: boolean;
  /** The item this hop arrives at has ended: drawn dimmed. */
  isPast?: boolean;
}

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

function formatDistance(distanceKm: number): string {
  const miles = distanceKm * KM_TO_MILES;
  return miles < 1 ? `${miles.toFixed(1)} mi` : `${Math.round(miles)} mi`;
}

/**
 * A travel pin in the day column's left gutter: a dotted rail spanning the
 * gap between two consecutive items, a mode badge at its midpoint, and (when
 * the gap is tall enough) the duration beside it. The rail and pin never
 * overlap a block. Hovering, focusing, or tapping the pin opens a card to
 * change this pin's mode and open Google Maps directions.
 */
export function TravelConnector({
  segment,
  gapTop,
  gapBottom,
  isReadOnly,
  isPast = false,
}: TravelConnectorProps) {
  const pinRef = useRef<HTMLButtonElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { segmentKey } = segment;
  const open = useUIStore((s) => s.openTravelPinKey === segmentKey);
  const setOpenTravelPinKey = useUIStore((s) => s.setOpenTravelPinKey);
  const setTravelMode = useTripStore((s) => s.setTravelMode);

  const clearTimer = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  };

  const show = useCallback(
    () => setOpenTravelPinKey(segmentKey),
    [segmentKey, setOpenTravelPinKey],
  );
  const hide = useCallback(() => {
    if (useUIStore.getState().openTravelPinKey === segmentKey) {
      setOpenTravelPinKey(null);
    }
  }, [segmentKey, setOpenTravelPinKey]);

  const focusInside = () => {
    const active = document.activeElement;
    return !!active && !!cardRef.current?.contains(active);
  };

  const scheduleOpen = () => {
    clearTimer();
    timer.current = setTimeout(show, OPEN_DELAY_MS);
  };
  const scheduleClose = () => {
    clearTimer();
    timer.current = setTimeout(() => {
      // A focused control (the mode dropdown) keeps the card open while the
      // pointer is over its native popup, which sits outside the card.
      if (!focusInside()) hide();
    }, CLOSE_DELAY_MS);
  };

  useEffect(() => clearTimer, []);

  // Touch has no hover: a tap toggles the card and a tap elsewhere closes it.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (pinRef.current?.contains(target)) return;
      if (cardRef.current?.contains(target)) return;
      hide();
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    return () =>
      document.removeEventListener("pointerdown", onPointerDown, true);
  }, [open, hide]);

  const closeAndRefocus = () => {
    clearTimer();
    hide();
    pinRef.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape" && open) {
      event.stopPropagation();
      closeAndRefocus();
    }
  };

  const onBlur = (event: React.FocusEvent) => {
    const next = event.relatedTarget as Node | null;
    if (
      next &&
      (pinRef.current?.contains(next) || cardRef.current?.contains(next))
    )
      return;
    scheduleClose();
  };

  const {
    from,
    to,
    resolvedMode,
    modeSource,
    travelMinutes,
    distanceKm,
    source,
    gapMinutes,
    overrun,
  } = segment;
  // Open pin and card share one outline colour so they read as a callout
  const accent = overrun
    ? { border: "border-error", text: "text-error" }
    : { border: "border-info", text: "text-info" };
  const gapHeight = Math.max(gapBottom - gapTop, 0);
  const midY = gapTop + gapHeight / 2;
  const approx = source === "estimate" ? "~" : "";
  const duration = `${approx}${formatMinutes(travelMinutes)}`;
  const gap = Math.max(Math.round(gapMinutes), 0);
  const modeLabel = TRAVEL_MODE_LABELS[resolvedMode].toLowerCase();
  const summary = overrun
    ? `${duration} ${modeLabel} to ${to.name}, but only ${gap} min between items`
    : `${duration} ${modeLabel} to ${to.name}, ${formatDistance(distanceKm)}`;

  const modeChoice: TravelModeChoice =
    modeSource === "override" ? segment.mode : "auto";
  // An automatic transit pin Google couldn't route shows its drive fallback
  const autoMode = modeSource === "auto" ? resolvedMode : segment.autoMode;

  return (
    <>
      {gapHeight > 0 && (
        <div
          className={clsx(
            "pointer-events-none absolute z-[3] -translate-x-1/2 border-l-2 border-dotted",
            overrun ? "border-error" : "border-calendar-travel-rail",
            isPast && "calendar-past",
          )}
          style={{ left: TRAVEL_RAIL_X_PX, top: gapTop, height: gapHeight }}
          aria-hidden
        />
      )}

      <Button
        ref={pinRef}
        variant="unstyled"
        className={clsx(
          "absolute z-[15] flex size-4 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full shadow-sm",
          isPast && !open && "calendar-past",
          "cursor-pointer transition-transform hover:scale-125 focus-visible:scale-125 focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-info",
          open
            ? clsx("scale-125 border-2 bg-surface", accent.border, accent.text)
            : overrun
              ? "border border-error bg-error-bg-strong text-error"
              : "border border-border bg-calendar-travel-pin-bg text-calendar-travel-pin-text",
        )}
        style={{ left: TRAVEL_RAIL_X_PX, top: midY }}
        aria-label={`Travel: ${summary}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        data-calendar-transient-control
        data-travel-pin={segmentKey}
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onFocus={() => {
          clearTimer();
          show();
        }}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        onPointerDown={(event) => event.stopPropagation()}
        onClick={(event) => {
          event.stopPropagation();
          clearTimer();
          if (open) hide();
          else show();
        }}
      >
        <span
          className="material-symbols-outlined calendar-travel-pin-icon leading-none"
          aria-hidden
        >
          {TRAVEL_MODE_ICONS[resolvedMode]}
        </span>
      </Button>

      {gapHeight >= LABEL_MIN_GAP_PX && (
        <span
          className={clsx(
            "pointer-events-none absolute z-[3] -translate-y-1/2 whitespace-nowrap text-2xs font-bold tabular-nums",
            overrun ? "text-error" : "text-secondary",
            isPast && "calendar-past",
          )}
          style={{ left: TRAVEL_RAIL_X_PX + 12, top: midY }}
          aria-hidden
          data-calendar-transient-control
        >
          {duration}
          {overrun && ` · ${gap} min gap`}
        </span>
      )}

      {open && (
        <CalendarAnchoredPopover
          anchorRef={pinRef}
          role="dialog"
          aria-label={`Travel from ${from.name} to ${to.name}`}
          offset={CARD_OFFSET_PX}
          className={clsx(
            "group w-64 rounded-xl border-2 bg-surface p-3 shadow-lg",
            accent.border,
          )}
          onMouseEnter={clearTimer}
          onMouseLeave={scheduleClose}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
        >
          {/* Callout arrow: shares the card's border colour and covers the
              card border where it attaches, so card and pin read as one */}
          <span
            aria-hidden
            className={clsx(
              "pointer-events-none absolute size-3 -translate-y-1/2 rotate-45 bg-surface",
              accent.border,
              "group-data-[side=right]:-left-1.5 group-data-[side=right]:border-b-2 group-data-[side=right]:border-l-2",
              "group-data-[side=left]:-right-1.5 group-data-[side=left]:border-r-2 group-data-[side=left]:border-t-2",
            )}
            style={{ top: "var(--popover-anchor-y)" }}
          />
          <div ref={cardRef} className="relative flex flex-col gap-2">
            <div className="min-w-0">
              <p className="m-0 text-2xs font-extrabold uppercase tracking-wider text-tertiary">
                Travel
              </p>
              {/* Origin over destination, joined like the calendar rail */}
              <ol className="m-0 mt-1 grid list-none grid-cols-[auto_minmax(0,1fr)] items-center gap-x-2 p-0">
                <li className="contents">
                  <span
                    className={clsx(
                      "size-2 justify-self-center rounded-full border-2 bg-surface",
                      accent.border,
                    )}
                    aria-hidden
                  />
                  <span
                    className="truncate text-xs font-bold text-primary"
                    title={from.name}
                  >
                    <span className="sr-only">From </span>
                    {from.name}
                  </span>
                </li>
                <li className="contents" aria-hidden>
                  <span className="h-2 justify-self-center border-l-2 border-dotted border-calendar-travel-rail" />
                  <span />
                </li>
                <li className="contents">
                  <span
                    className={clsx(
                      "size-2 justify-self-center rounded-full",
                      overrun ? "bg-error" : "bg-info",
                    )}
                    aria-hidden
                  />
                  <span
                    className="truncate text-xs font-bold text-primary"
                    title={to.name}
                  >
                    <span className="sr-only">To </span>
                    {to.name}
                  </span>
                </li>
              </ol>
            </div>

            <div className="flex items-center gap-1.5 text-sm font-bold text-primary tabular-nums">
              <TravelModePicker
                mode={resolvedMode}
                choice={modeChoice}
                autoMode={autoMode}
                disabled={isReadOnly}
                onChange={(choice) =>
                  setTravelMode(
                    segment.targetId,
                    choice === "auto" ? null : choice,
                  )
                }
              />
              {duration}
              <span className="font-semibold text-secondary">
                · {formatDistance(distanceKm)}
              </span>
              <span className="ml-auto text-2xs font-semibold text-tertiary">
                {source === "routes" ? "Google" : "Estimate"}
              </span>
            </div>

            <p
              className={clsx(
                "m-0 text-xs",
                overrun ? "font-bold text-error" : "text-secondary",
              )}
            >
              {overrun
                ? `Only ${gap} min between items`
                : `${gap} min between items`}
            </p>

            <a
              href={directionsMapsUrl(from.coords, to.coords, resolvedMode)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary justify-center no-underline"
            >
              <span className="material-symbols-outlined text-base" aria-hidden>
                directions
              </span>
              Open in Google Maps
            </a>
          </div>
        </CalendarAnchoredPopover>
      )}
    </>
  );
}
